/*
 * -----------------------------------------------------------------------
 * <copyright file="convertor.ts" company="Triumph Research Intelligence">
 * Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 * </copyright>
 * -----------------------------------------------------------------------
 *
 */

import { ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';
import i18n from 'i18next';
import moment from 'moment';
import { getFormat } from './generator';
import { DataReviewTableColumnType, NamedItemType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { RuleFields } from '@queries/generated/fragments.generated';
import { SubjectReviewStatus } from '@components-clean/subject-monitoring/subject-review/subject-review-instance/subject-review-constants';
import {
    SubjectDataListingReviewStatusType,
    SubjectDataListingReviewStatusTypes,
} from '@interfaces-clean/subject-monitoring/subject-data-listing-review';

const DAILY = 'DAILY';
const WEEKLY = 'WEEKLY';
const MONTHLY = 'MONTHLY';
const QUARTERLY = 'QUARTERLY';
const ANNUALLY = 'ANNUALLY';

const convertFrequencyUnit = (amount: number, unit: string): string => {
    switch (unit) {
        case DAILY:
            return i18n.t(amount === 1 ? 'day' : 'days');
        case WEEKLY:
            return i18n.t(amount === 1 ? 'week' : 'weeks');
        case MONTHLY:
            return i18n.t(amount === 1 ? 'month' : 'months');
        case QUARTERLY:
            return i18n.t(amount === 1 ? 'quarter' : 'quarters');
        default:
            return '';

        case ANNUALLY:
            if (amount === 1) {
                return i18n.t('year');
            }
            return i18n.t('years');
    }
};

const convertReviewFrequency = (amount: number, unit: string): string | null => {
    if (amount === 0 || !unit || unit === 'NONE') return '';

    const convertedUnit = convertFrequencyUnit(amount, unit);

    return `${amount} ${convertedUnit}`;
};

// METHOD MARKED AS DEPRECATED
// Do not use this method in any new code
// Instead, use one of the more explicit methods below, or consider whether any date conversion is needed in the first place
// In general, a date needs to be converted and treated as utc if getting it from the API, e.g., via results of a query,
// This is done by stringDateWithoutTimeFromUtc
// It usually does not need to be converted at all if it is from component's state,
// as it should already have been converted into correct format by this point,
// and it is usually just converting a correct string into a date and then back into the correct string again
const formatDateUTC_DEPRECATED = (
    date: string | Date | any,
    isUtc: boolean = false,
    format: string = 'YYYY-MM-DD',
): string | Date | null => {
    if (date) return isUtc ? moment(date, 'YYYY-MM-DD[T]HH:mm:ss[Z]').format(format) : moment(date).format(format);
    return null;
};

// Converts a Date which is in Utc format into the Unix epoch value. The returned date will be the same as the input
// and not change regardless of timezones or daylight savings. If the input is null it will return null.
const unixDateFromUtc = (utcBasedInput: Date | string, ignoreTime?: boolean, ignoreSeconds?: boolean) => {
    const utcDate = moment(utcBasedInput, 'YYYY-MM-DD[T]HH:mm:ss[Z]');
    if (ignoreTime) utcDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    if (ignoreSeconds) utcDate.set({ second: 0, millisecond: 0 });

    return utcBasedInput ? utcDate.unix() : null;
};

// Converts a Date which is in Utc format into a date string. The returned date will be the same as the input
// and not change regardless of timezones or daylight savings. If the input is null it will return null.
// This is usually the method we want to use when getting a date from the API, e.g., via results of a query,
const stringDateWithoutTimeFromUtc = (
    utcBasedInput: Date | string | null | undefined,
    format: string = 'YYYY-MM-DD',
): string | null => {
    if (utcBasedInput) {
        const utcDate = moment.utc(utcBasedInput);
        return utcDate.year() > 1 ? utcDate.format(format) : null;
    }

    return null;
};

// Converts a Date which is in Utc format into a date string. The returned date will be the same as the input
// but will return an empty string, rather than null when no date is provided.
const stringDateWithoutTimeFromUtcNonNullable = (
    utcBasedInput: Date | string | null | undefined,
    format: string = 'YYYY-MM-DD',
): string => {
    return stringDateWithoutTimeFromUtc(utcBasedInput, format) ?? '';
};

const stringDateWithTimeFromUtcNonNullable = (
    utcBasedInput: Date | string | null | undefined,
    format: string = 'YYYY-MM-DD HH:mm:ss',
): string | null => {
    if (utcBasedInput) {
        const utcDate = moment.utc(utcBasedInput);
        return utcDate.year() > 1 ? utcDate.format(format) : '';
    }

    return '';
};

// Converts a Date which is in UTC format into a javascript Date object. The returned date will be the same as the input
// and not change regardless of timezones or daylight savings. If the input is null it will return null.
const javascriptDateWithoutTimeFromUTC = (utcBasedInput: Date | string | null): Date | null => {
    if (utcBasedInput === null) return null;

    const momentUTCDate = moment.utc(utcBasedInput);

    return new Date(momentUTCDate.year(), momentUTCDate.month(), momentUTCDate.date());
};

// Converts a Date which is in UTC format into a javascript Date object with time. The returned date will be the same as the input
// and not change regardless of timezones or daylight savings. If the input is null it will return null.
const javascriptDateWithTimeFromUTC = (utcBasedInput: Date | string | null): Date | null => {
    if (utcBasedInput === null) return null;

    const momentUTCDate = moment.utc(utcBasedInput);

    return new Date(
        momentUTCDate.year(),
        momentUTCDate.month(),
        momentUTCDate.date(),
        momentUTCDate.hours(),
        momentUTCDate.minutes(),
        momentUTCDate.seconds(),
    );
};

// Converts a Date which is in Local format into a date string. The returned date will be the same as the input
// and not change regardless of timezones or daylight savings. If the input is null it will return null.
const stringDateWithoutTimeFromLocal = (localBasedInput: Date | string, format?: string): string | null => {
    if (localBasedInput) {
        return moment(localBasedInput).format(format ?? 'YYYY-MM-DD');
    }

    return null;
};

// Gets the current date in the UTC timezone, formatted as a string
const stringDateWithoutTimeFromCurrentUtcDate = (format: string = 'YYYY-MM-DD') => moment.utc().format(format);

// Converts a date string into a UTC Javascript Date, regardless of the timezone/format of the input string
// This varies slightly from javascriptDateWithoutTimeFromUTC, as given an example input of "31 Oct 2025",
// the returned javascript date object from both methods may look the same, passing them to `stringDateWithoutTimeFromUtc`
// could result in different values if the machine's timezone is not UTC, as javascriptDateWithoutTimeFromUTC expects a UTC formatted date
const javascriptDateWithoutTimeFromString = (dateString: string | null): Date | null => {
    if (dateString === null) return null;

    const momentUTCDate = moment.utc(dateString);

    return new Date(Date.UTC(momentUTCDate.year(), momentUTCDate.month(), momentUTCDate.date()));
};

const convertNamedType = (type: string) => {
    switch (type) {
        case NamedItemType.RISK_CONTROL_STRATEGY:
            return 'Risk Control Strategy';
        case NamedItemType.CONTROL_TYPE:
            return 'Control Type';
        case NamedItemType.RISK_OBSERVATION_CLASSIFICATION:
            return 'Observation Classification';
        case NamedItemType.RISK_CATEGORY:
            return 'Risk Category';
        case NamedItemType.RISK_LIBRARY_CATEGORY:
            return 'Risk Library Category';
        case NamedItemType.RESPONSIBLE_FUNCTION:
            return 'Responsible Function';
        case NamedItemType.REVIEW_LOG_DECISION:
            return 'Review Log Decision';
        case NamedItemType.VENDOR:
            return 'Vendor';
        case NamedItemType.CTQ_TYPE:
            return 'CtQ Type';
        case NamedItemType.CTQEDC_ENTRY:
            return 'CtQ EDC Entry';
        case NamedItemType.CONTROLS_REVIEW_LOG_DECISION:
            return 'Controls Review Log Decision';
        case NamedItemType.CONTROL_PHASE:
            return 'Control Phase';
        case NamedItemType.RISK_QUALITY_ISSUE:
            return 'Risk Quality Issue';
        case NamedItemType.SM_REVIEW_LOG_DECISION:
            return 'SM Review Log Decision';
        default:
            return '';
    }
};

const convertDateToDays = (amount: any, type: any) => {
    if (type === 'NONE') return;
    let days = 0;

    // defined const for month
    const MONTH = 30.42;

    if (type === ANNUALLY) {
        days = MONTH * 12 * amount;
    } else if (type === QUARTERLY) {
        days = MONTH * 3 * amount;
    } else if (type === MONTHLY) {
        days = MONTH * amount;
    } else if (type === DAILY) {
        days = 1 * amount;
    } else if (type === WEEKLY) {
        days = 7 * amount;
    }

    return days;
};

const getLabels = (ids: string[] | null, arrayOfItems: any, fieldName?: string): string => {
    const result = ids?.length
        ? arrayOfItems?.filter((el: any) => ids.indexOf(el.id) >= 0).map((el: any) => el[fieldName])
        : [];
    return result?.length ? result.join(', ') : '';
};

const { CM, CM2, RAM, ISMP, SM, DQ } = Constants.Modules;

const getIsActiveField = (field: string, model: number) => {
    // (CM=1, CM2=8, RAM=2, ISMP=4, SM=16, DQ=32).
    switch (field) {
        case 'CM':
            // eslint-disable-next-line no-bitwise
            return !!(model & CM);

        case 'CM2':
            // eslint-disable-next-line no-bitwise
            return !!(model & CM2);

        case 'RAM':
            // eslint-disable-next-line no-bitwise
            return !!(model & RAM);

        case 'ISMP':
            // eslint-disable-next-line no-bitwise
            return !!(model & ISMP);

        case 'SM':
            // eslint-disable-next-line no-bitwise
            return !!(model & SM);

        case 'DQ':
            // eslint-disable-next-line no-bitwise
            return !!(model & DQ);

        default:
            return false;
    }
};

const getActiveModules = (model: number) => {
    const modules = [];
    if (model & CM) modules.push('CM');
    if (model & CM2) modules.push('CM2');
    if (model & RAM) modules.push('RAM');
    if (model & ISMP) modules.push('ISMP');
    if (model & SM) modules.push('SM');
    if (model & DQ) modules.push('DQ');
    return modules.join(', ');
};

const pad = (s: string) => (s.length === 1 ? `0${s}` : s);
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const doFormat = (date: any) =>
    date ? `${pad(date.getUTCDate().toString())}-${months[date.getUTCMonth()]}-${date.getUTCFullYear()}` : null;

const modifyObjectForExport = (item: any, type: number, fieldNum: number, value: any, columnType?: number) => {
    if (type === Constants.FieldDefinitionFieldTypes.BOOL)
        item[`_field-${fieldNum}`] = value === true ? 'Yes' : value === false ? 'No' : '';
    else if (type === Constants.FieldDefinitionFieldTypes.DATE)
        item[`_field-${fieldNum}`] = value !== null ? doFormat(new Date(value)) : '';
    else if (type === Constants.FieldDefinitionFieldTypes.PARTIAL_DATE) {
        const format = getFormat(value.ignore);
        item[`_field-${fieldNum}`] = {
            time:
                value.time !== null
                    ? format === 'DD-MMM-YYYY'
                        ? doFormat(new Date(value.time))
                        : formatDateUTC_DEPRECATED(value.time, true, format)
                    : '',
        };
    } else if (type === Constants.FieldDefinitionFieldTypes.FLOAT) {
        const roundTo =
            Constants.DataReviewTableColumnType[columnType] === DataReviewTableColumnType.PERCENTAGE ? 0 : 2;
        item[`_field-${fieldNum}`] = value !== null ? parseFloat(value ?? 0).toFixed(roundTo) : '';
    }
    return item;
};

const convertBooleanToYesNo = (value: boolean | string) => {
    switch (value) {
        case true:
        case 'Yes':
            return 'Yes';
        case false:
        case 'No':
            return 'No';
        default:
            return '';
    }
};

export const convertYesNoToBoolean = (value: boolean | string | number | undefined | null) => {
    if (typeof value !== 'string') {
        return null;
    }

    const valueLow = value.toLowerCase();

    switch (true) {
        case valueLow === 'yes':
            return true;
        case valueLow === 'no':
            return false;
        default:
            return null;
    }
};

const convertDirectionEnumToString = (value: ScoreDirection) => {
    switch (value) {
        case ScoreDirection.Upper:
            return 'Increased';
        case ScoreDirection.Lower:
            return 'Decreased';
        case ScoreDirection.Matching:
            return 'No Change';
        default:
            return '';
    }
};

const convertStatusEnumToString = (value: SubjectReviewStatus) => {
    switch (value) {
        case SubjectReviewStatus.Added:
            return 'Added';
        case SubjectReviewStatus.Removed:
            return 'Removed';
        case SubjectReviewStatus.Reviewed:
            return 'Reviewed';
        default:
            return '';
    }
};

const convertSMDataListingReviewStatusToString = (value: SubjectDataListingReviewStatusType) => {
    switch (value) {
        case SubjectDataListingReviewStatusTypes.NotReviewed:
            return 'Not Reviewed';
        case SubjectDataListingReviewStatusTypes.Reviewed:
            return 'Reviewed';
        case SubjectDataListingReviewStatusTypes.Removed:
            return 'Removed';
        default:
            return '';
    }
};

const convertColorEnumToString = (value: number) => {
    return HC_RAG_NAMES[value];
};

/*
This code has been replicated in the PowerBI API in `Opra.PowerBIApi\Mappings\StudyToStudyDTOMap.cs`
in the method `FormRulesString`. If this code changes here, it MUST be updated in that file as well.
 */
const convertRules = (rulesArray: (RuleFields | null)[] | null) => {
    let rulesString = '';

    if (rulesArray?.length) {
        rulesArray.forEach((ruleItem, index: number) => {
            if (ruleItem) {
                rulesString = `${rulesString} ${ruleItem.comparatorField} ${i18n.t(
                    `admin:${ruleItem.ruleClauseOperator}`,
                )} ${ruleItem.value} ${
                    index === 0 && ruleItem.ruleClause && ruleItem.ruleClause !== 'NONE' ? ruleItem.ruleClause : ''
                }`;
            }
        });
    }

    return rulesString;
};

const labelToTestId = (label: string) => label.replaceAll(' ', '-').toLowerCase();

export {
    convertReviewFrequency,
    convertNextReviewDate,
    convertFilterField,
    unixDateFromUtc,
    formatDateUTC_DEPRECATED,
    javascriptDateWithoutTimeFromUTC,
    stringDateWithoutTimeFromUtcNonNullable,
    stringDateWithTimeFromUtcNonNullable,
    javascriptDateWithTimeFromUTC,
    stringDateWithoutTimeFromUtc,
    stringDateWithoutTimeFromLocal,
    stringDateWithoutTimeFromCurrentUtcDate,
    convertNamedType,
    getLabels,
    getIsActiveField,
    convertDateToDays,
    determineTypeOfColumn,
    getActiveModules,
    modifyObjectForExport,
    convertBooleanToYesNo,
    convertDirectionEnumToString,
    convertColorEnumToString,
    convertRules,
    labelToTestId,
    javascriptDateWithoutTimeFromString,
    convertStatusEnumToString,
    doFormat,
    convertSMDataListingReviewStatusToString,
};
